import React, { useMemo, useState } from "react";
import { Building2, User } from "lucide-react";
import moment from "moment-timezone";
import reminderIcon from "../../../Assets/Images/remainder-icon.svg";
import todayIcon from "../../../Assets/Images/today-icon.svg";
import overdueIcon from "../../../Assets/Images/overdue-icon.svg";
import tomorrowIcon from "../../../Assets/Images/tomorrow-icon.svg";
const GROUPS = [
  { key: "today", label: "Today" },
  { key: "overdue", label: "Overdue" },
  { key: "tomorrow", label: "Tomorrow" },
];

const isClientReminder = (item) =>
  Boolean(item.client_id || item.clientId) ||
  /client/i.test(item.type || item.entity_type || item.stage || "");

export default function AdminFollowUps({ followUps, loading, timezone }) {
  const [filter, setFilter] = useState("all");
  const allItems = useMemo(
    () => GROUPS.flatMap(({ key }) => followUps[key]),
    [followUps],
  );
  const clients = allItems.filter(isClientReminder).length;
  const counts = {
    all: allItems.length,
    candidates: allItems.length - clients,
    clients,
  };
  const visibleGroups = GROUPS.map((group) => ({
    ...group,
    items: followUps[group.key].filter((item) => {
      const isClient = isClientReminder(item);
      return filter === "all" || (filter === "clients" ? isClient : !isClient);
    }),
  }));
  const visibleCount = visibleGroups.reduce(
    (total, group) => total + group.items.length,
    0,
  );

  return (
    <section
      className="admin-followups"
      aria-labelledby="admin-followups-title"
      aria-busy={loading}
    >
      <div className="admin-followups-heading">
        <div>
          <h3 id="admin-followups-title">Follow-ups</h3>
          <p>Reminders that need your attention.</p>
        </div>
      </div>
      <div className="admin-followups-summary">
        {[
          {
            label: "Total reminders",
            count: allItems.length,
            icon: reminderIcon,
            tone: "total",
          },
          {
            label: "Due today",
            count: followUps.today.length,
            icon: todayIcon,
            tone: "today",
          },
          {
            label: "Overdue",
            count: followUps.overdue.length,
            icon: overdueIcon,
            tone: "overdue",
          },
          {
            label: "Tomorrow",
            count: followUps.tomorrow.length,
            icon: tomorrowIcon,
            tone: "tomorrow",
          },
        ].map(({ label, count, icon, tone }) => (
          <div className={`admin-followups-stat ${tone}`} key={tone}>
            <img
              className="admin-followups-stat-icon"
              src={icon}
              alt=""
              width={34}
              height={34}
            />
            <div>
              <strong>{loading ? "—" : count}</strong>
              <span>{label}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="admin-followups-toolbar">
        <div
          className="admin-followups-filters"
          role="group"
          aria-label="Follow-up type"
        >
          {[
            ["all", "All"],
            ["candidates", "Candidates"],
            ["clients", "Clients"],
          ].map(([key, label]) => (
            <button
              type="button"
              key={key}
              aria-pressed={filter === key}
              className={filter === key ? "active" : ""}
              onClick={() => setFilter(key)}
            >
              {label} <span>{loading ? "—" : counts[key]}</span>
            </button>
          ))}
        </div>
      </div>
      <div
        className="admin-followups-list"
        tabIndex={0}
        role="region"
        aria-label="Follow-up reminders"
      >
        {loading ? (
          <p className="admin-followups-empty" role="status">
            Loading follow-ups...
          </p>
        ) : visibleCount === 0 ? (
          <p className="admin-followups-empty" role="status">
            {allItems.length
              ? "No follow-ups match this filter."
              : "Your pending follow-ups will appear here."}
          </p>
        ) : (
          visibleGroups.map(
            ({ key, label, items }) =>
              items.length > 0 && (
                <div className={`admin-followups-group ${key}`} key={key}>
                  <h4>
                    {label} <span>· {items.length}</span>
                  </h4>
                  {items.map((item, index) => {
                    const isClient = isClientReminder(item);
                    const name = item.name || "Unnamed contact";
                    const initials =
                      item.initials ||
                      name
                        .split(/\s+/)
                        .slice(0, 2)
                        .map((word) => word[0])
                        .join("");
                    const callback = item.callback_at
                      ? moment(item.callback_at).tz(timezone)
                      : null;
                    const validDate = callback?.isValid();
                    const TypeIcon = isClient ? Building2 : User;
                    return (
                      <div
                        className="admin-followups-row"
                        key={`${key}-${item.id || index}-${index}`}
                      >
                        <span
                          className={`admin-followups-avatar ${isClient ? "client" : "candidate"}`}
                        >
                          {initials}
                        </span>
                        <div className="admin-followups-contact">
                          <span className="admin-followups-name" title={name}>
                            {name}
                          </span>
                          <span
                            className={`admin-followups-badge ${isClient ? "client" : "candidate"}`}
                          >
                            <TypeIcon size="1.25em" aria-hidden="true" />
                            {isClient
                              ? "Client callback"
                              : "Candidate callback"}
                          </span>
                        </div>
                        <div className="admin-followups-date">
                          <span>
                            {validDate
                              ? callback.format("h:mm a z")
                              : "Time not set"}
                          </span>
                          <small>
                            {validDate ? callback.format("D MMM YYYY") : ""}
                          </small>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ),
          )
        )}
      </div>
    </section>
  );
}
